import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Asterisk, Download, Loader2, AlertTriangle } from "lucide-react";
import { when } from "@/components/claude/ClaudeBadge";

const fileName = (cd, fallback) => {
  const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd || "");
  return m ? decodeURIComponent(m[1] || m[2]) : fallback;
};

export default function ClaudeDownload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/claude/downloads/${token}`).then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This download link isn't valid."));
  }, [token]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(info.path, { responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName(r.headers["content-disposition"], `${info.bid_name || "GridForge"} - ${info.label}`);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Could not create the file. Try again in a moment."); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-12" data-testid="claude-download-page">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3"><Asterisk className="w-4 h-4 text-[#D97757]" /><h1 className="font-semibold text-black">File from Claude for review</h1></div>
        {err ? (
          <div data-testid="claude-download-error" className="flex gap-2 text-sm text-slate-700"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />{err}</div>
        ) : !info ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#003E87]" />
        ) : (
          <>
            <div className="text-sm text-slate-700" data-testid="claude-download-label"><b>{info.label}</b> for <Link to={`/bids/${info.bid_id}`} className="text-[#003E87] hover:underline">{info.bid_name}</Link></div>
            <div className="text-xs text-slate-400 mt-1">For your review only. It hasn't been sent to anyone. Link works until {when(info.expires_at)}.</div>
            <Button data-testid="claude-download-btn" disabled={busy} onClick={download} className="mt-4 bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}{busy ? "Preparing…" : "Download"}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
